import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const statusBadge = {
  in_progress: { label: 'In Progress', className: 'badge badge-approved' },
  on_hold: { label: 'On Hold', className: 'badge badge-pending' },
  completed: { label: 'Completed', className: 'badge badge-holiday' },
};

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', client_name: '', site_address: '', notes: '' });
  const [view, setView] = useState('active');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/jobs${view === 'archived' ? '?view=archived' : ''}`);
      setJobs(res.data);
    } catch (err) {
      setError('Could not load jobs.');
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { load(); }, [load]);

  const createJob = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await api.post('/api/jobs', form);
      setShowNew(false);
      setForm({ name: '', client_name: '', site_address: '', notes: '' });
      navigate(`/jobs/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create job.');
    }
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title">Jobs in Progress</h1>
          <p className="page-subtitle">Every live build in one place. Click a job to manage its documents.</p>
        </div>
        <button className="btn btn-gold" onClick={() => setShowNew(true)}>+ New Job</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '6px', padding: '4px', width: 'fit-content' }}>
        {['active', 'archived'].map(v => (
          <button key={v} onClick={() => setView(v)} style={{
            padding: '8px 18px', border: 'none', borderRadius: '4px', cursor: 'pointer',
            fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: '500', letterSpacing: '0.5px',
            background: view === v ? 'var(--navy)' : 'transparent',
            color: view === v ? 'var(--white)' : 'var(--grey-mid)',
            transition: 'all 0.15s'
          }}>
            {v === 'active' ? 'Active' : 'Archived'}
          </button>
        ))}
      </div>

      {loading ? <div className="loader" /> : jobs.length === 0 ? (
        <div className="card"><div className="card-body"><div className="empty-state">
          {view === 'archived' ? 'No archived jobs.' : 'No jobs yet. Create the first one.'}
        </div></div></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
          {jobs.map(j => {
            const badge = statusBadge[j.status] || statusBadge.in_progress;
            const met = Number(j.required_met);
            const req = Number(j.required_count);
            const complete = met >= req;
            return (
              <div
                key={j.id}
                className="card"
                onClick={() => navigate(`/jobs/${j.id}`)}
                style={{ cursor: 'pointer', transition: 'transform 0.15s, border-color 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = 'var(--gold)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.borderColor = ''; }}
              >
                <div className="card-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <h3 style={{ color: 'var(--navy)', fontFamily: 'Cormorant Garamond, serif', fontSize: 20, fontWeight: 500, margin: 0 }}>
                      {j.name}
                    </h3>
                    <span className={badge.className}>{badge.label}</span>
                  </div>
                  {j.client_name && <div style={{ color: 'var(--gold-dark)', fontSize: 13, marginTop: 6 }}>{j.client_name}</div>}
                  {j.site_address && <div style={{ color: 'var(--grey-mid)', fontSize: 12, marginTop: 4 }}>{j.site_address}</div>}

                  <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, color: complete ? 'var(--success)' : 'var(--warning)' }}>
                      {complete ? '✓ Documents complete' : `Documents ${met}/${req}`}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--grey-mid)' }}>
                      {j.file_count} file{Number(j.file_count) === 1 ? '' : 's'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showNew && (
        <div className="modal-overlay" onClick={() => setShowNew(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">New Job</h3>
              <button className="modal-close" onClick={() => setShowNew(false)}>×</button>
            </div>
            <form onSubmit={createJob}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Job Name *</label>
                  <input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. The Oaks, Plot 4" />
                </div>
                <div className="form-group">
                  <label className="form-label">Client Name</label>
                  <input className="form-control" value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Site Address</label>
                  <input className="form-control" value={form.site_address} onChange={e => setForm({ ...form, site_address: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowNew(false)}>Cancel</button>
                <button type="submit" className="btn btn-gold">Create Job</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;
